
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { user, session, loading, refreshSession, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAttemptingRefresh, setIsAttemptingRefresh] = useState(false);
  const [refreshAttempted, setRefreshAttempted] = useState(false);
  const [refreshTimeoutId, setRefreshTimeoutId] = useState<number | null>(null);
  const [forcingLogout, setForcingLogout] = useState(false);
  const isMobile = useIsMobile();
  
  // Timeouts otimizados para mobile com base na experiência do usuário
  const REFRESH_TIMEOUT = isMobile ? 25000 : 12000; // 25 segundos para mobile, 12 para desktop
  const INITIAL_LOAD_TIMEOUT = isMobile ? 8000 : 5000; // Timeout mais curto para carregamento inicial

  // Force logout function with improved error handling
  const handleForceLogout = async () => {
    console.log('Force logout initiated');
    
    if (forcingLogout) {
      return;
    }
    
    setForcingLogout(true);
    
    if (refreshTimeoutId) {
      window.clearTimeout(refreshTimeoutId);
      setRefreshTimeoutId(null);
    }
    
    setIsAttemptingRefresh(false);
    setRefreshAttempted(true);
    
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error during force logout');
      // Ensure we still redirect even if signOut fails
      navigate('/login', { replace: true });
    } finally {
      setForcingLogout(false);
    }
  };

  useEffect(() => {
    console.log('Auth state:', { 
      isAuthenticated: !!user, 
      loading, 
      refreshAttempted, 
      isAttemptingRefresh,
      isMobile,
      sessionExpiry: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'No session'
    });

    // If user is authenticated, no need to do anything
    if (user) {
      return;
    }

    // If loading, just wait
    if (loading) {
      return;
    }

    // If we've already attempted a refresh or are in the middle of one, don't try again
    if (refreshAttempted || isAttemptingRefresh) {
      return;
    }

    // No user, not loading, no refresh attempt yet - let's try to refresh the session
    const attemptRefresh = async () => {
      console.log('Starting session refresh attempt');
      setIsAttemptingRefresh(true);
      
      try {
        // Set a timeout to abandon refresh attempt after specified timeout
        const timeoutId = window.setTimeout(() => {
          console.log(`Session refresh timed out after ${REFRESH_TIMEOUT/1000}s`);
          toast.error(`Tempo limite excedido (${REFRESH_TIMEOUT/1000}s)`, {
            description: 'Falha ao reconectar'
          });
          handleForceLogout();
        }, REFRESH_TIMEOUT);
        
        setRefreshTimeoutId(timeoutId);
        
        // Attempt to refresh the session
        const success = await refreshSession();
        
        // Clear timeout if refresh completed before timeout
        if (refreshTimeoutId) {
          window.clearTimeout(timeoutId);
          setRefreshTimeoutId(null);
        }
        
        setRefreshAttempted(true);
        setIsAttemptingRefresh(false);
        
        if (!success) {
          console.log('Session refresh failed');
          toast.error('Falha ao reconectar', {
            description: 'Por favor, faça login novamente'
          });
          await handleForceLogout();
        } else {
          // Se estamos em um dispositivo móvel e o refresh foi bem-sucedido
          if (isMobile && success) {
            toast.success('Sessão restaurada com sucesso');
          }
        }
      } catch (error) {
        console.error('Error refreshing session');
        toast.error('Erro ao verificar sessão', {
          description: 'Por favor, faça login novamente'
        });
        await handleForceLogout();
      }
    };

    // Attempt to refresh the session
    attemptRefresh();
    
    // Cleanup timeout if component unmounts
    return () => {
      if (refreshTimeoutId) {
        window.clearTimeout(refreshTimeoutId);
      }
    };
  }, [user, loading, refreshAttempted, isAttemptingRefresh, isMobile]);

  // Show loading when we're verifying authentication or attempting refresh
  if (loading || isAttemptingRefresh) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-12 w-12 text-blue-dark animate-spin mb-4" />
        <p className="text-gray-600">Verificando autenticação...</p>
        <p className="text-sm text-gray-500 mt-2">
          {isMobile 
            ? `Se isso demorar mais de ${REFRESH_TIMEOUT/1000} segundos, você será redirecionado para fazer login novamente.`
            : `Se isso demorar mais de ${REFRESH_TIMEOUT/1000} segundos, você será redirecionado para fazer login novamente.`}
        </p>
        <Button 
          variant="outline" 
          className="mt-6" 
          onClick={handleForceLogout}
          disabled={forcingLogout}
        >
          {forcingLogout ? 'Saindo...' : 'Sair agora'}
        </Button>
      </div>
    );
  }

  // After refresh attempt, if still no user, redirect to login
  if (!user) {
    console.log('Redirecting to login');
    // Redirect to the login page with the redirection parameter
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return <>{children}</>;
};

export default PrivateRoute;
